import React, { useContext, useCallback, useState, useRef, useEffect, useMemo } from "react";
import { latestPerMinute } from "./latestPerMinute";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import 'chartjs-adapter-moment';
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart as ChartJS } from "chart.js";
import AuthContext from "../../../contexts/auth-context";
import moment from 'moment';

import { ChartHeader, ResolutionToggle, applyChartTheme, CHART_THEME, sectionLineDataset, hoverGuidePlugin } from "../../Concession/charts/chartTheme";
import { StatTile } from "../../Concession/components/ConcessionUI";
import AnalysisWindow from "../../Concession/components/AnalysisWindow";
import "../retail.css";
ChartJS.register(zoomPlugin, annotationPlugin);

// Function to convert game start time to annotation timestamp
const convertGameStartTimeToAnnotation = (gameDate, gameStartTime) => {
  if (!gameDate || !gameStartTime) return null;
  
  const [hours, minutes] = gameStartTime.split(':').map(Number);
  const annotationTime = new Date(`${gameDate}T${gameStartTime}:00`);
  
  return annotationTime.toISOString();
};

// More distinct colors for transaction locations
const TRANSACTION_COLORS = [
  '#2563eb',  // Strong blue - Checkout
  '#e11d48',  // Rose/red - Self Checkout
  '#059669',  // Emerald green
  '#d97706',  // Amber
  '#7c3aed',  // Violet
];

// Format seconds to M:SS
const formatSecondsToMinSec = (seconds) => {
  if (seconds === null || seconds === undefined) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const TransactionChart = ({ transactionData, isToday, currentLocation, dayType, setAutoFetchEnabled, currentGame }) => {
  const authCtx = useContext(AuthContext);
  const [dataValuesByLocation, setDataValuesByLocation] = useState({});
  const [loading, setLoading] = useState(true);
  const chartRef = useRef(null);
  
  // Add chart resolution state
  const [chartResolution, setChartResolution] = useState(1);
  const resolutions = [1, 5, 10, 15, 30, 60];
  const [dataTimeRange, setDataTimeRange] = useState({ min: null, max: null });

  // Time range state - using applied times for filtering (only for historical data)
  const [appliedStartTime, setAppliedStartTime] = useState(null);
  const [appliedEndTime, setAppliedEndTime] = useState(null);

  // Get all transaction location IDs
  const transactionLocationIds = useMemo(() => {
    if (!transactionData || typeof transactionData !== 'object') return [];
    return Object.keys(transactionData).map(Number).sort((a, b) => a - b);
  }, [transactionData]);

  const getIntervalKey = (timestamp, resolution = 1) => {
    const date = new Date(timestamp);
    date.setMinutes(Math.floor(date.getMinutes() / resolution) * resolution, 0, 0);
    return date.toISOString();
  };

  // Find the earliest and latest time points from available data (across all locations)
  const getDataTimeRange = () => {
    if (!transactionData || typeof transactionData !== 'object' || transactionLocationIds.length === 0) {
      return { earliest: null, latest: null };
    }
    
    const allTimePoints = [];
    transactionLocationIds.forEach(locId => {
      const locData = transactionData[locId] || [];
      locData.forEach(item => {
        allTimePoints.push(new Date(item.timestamp));
      });
    });
    
    if (allTimePoints.length === 0) return { earliest: null, latest: null };
    
    const earliest = new Date(Math.min(...allTimePoints));
    const latest = new Date(Math.max(...allTimePoints));
    return { earliest, latest };
  };

  const { earliest: earliestTime, latest: latestTime } = getDataTimeRange();

  // Handle time range changes from the slider (only for historical data)
  const handleTimeRangeChange = (startTime, endTime) => {
    if (!isToday) {
      setAppliedStartTime(startTime);
      setAppliedEndTime(endTime);
    }
  };

  // Resolution control functions

  // Function to calculate the time range from processed data
  const calculateDataTimeRange = useCallback((dataByLocation) => {
    const allTimes = [];
    Object.values(dataByLocation).forEach(data => {
      data.forEach(point => allTimes.push(new Date(point.x)));
    });
    
    if (allTimes.length === 0) return { min: null, max: null };
    
    const minTime = new Date(Math.min(...allTimes));
    const maxTime = new Date(Math.max(...allTimes));
    
    return { min: minTime, max: maxTime };
  }, []);

  // Enable auto-fetch for today's data
  useEffect(() => {
    if (isToday) {
      setAutoFetchEnabled(true);
    }
  }, [isToday, setAutoFetchEnabled]);

  // Filter data based on time range and update data map
  const updateDataMap = useCallback(() => {
    if (!transactionData || typeof transactionData !== 'object' || transactionLocationIds.length === 0) {
      setDataValuesByLocation({});
      setDataTimeRange({ min: null, max: null });
      setLoading(false);
      return;
    }

    const processedByLocation = {};

    transactionLocationIds.forEach(locId => {
      const locData = transactionData[locId] || [];
      
      // Use filtered data if we have applied values for historical data
      let dataToProcess = locData;
      
      if (!isToday && appliedStartTime && appliedEndTime) {
        dataToProcess = locData.filter(item => {
          const pointTime = new Date(item.timestamp);
          return pointTime >= appliedStartTime && pointTime <= appliedEndTime;
        });
      }
      
      // Aggregation by the selected resolution
      const dataMap = new Map();

      latestPerMinute(dataToProcess).forEach((item) => {
        const key = getIntervalKey(item.timestamp, chartResolution);
        const entry = dataMap.get(key) || { 
          totalTransactionTime: 0, 
          count: 0 
        };

        entry.totalTransactionTime += item.transactionTime || 0;
        entry.count++;

        dataMap.set(key, { ...entry, dateTime: key });
      });

      const aggregatedData = Array.from(dataMap.values()).map(
        ({ dateTime, totalTransactionTime, count }) => ({
          x: dateTime,
          transactionTime: Math.round(totalTransactionTime / count),
        })
      );

      // Sort by time
      aggregatedData.sort((a, b) => new Date(a.x) - new Date(b.x));

      processedByLocation[locId] = aggregatedData;
    });

    setDataValuesByLocation(processedByLocation);
    
    // Calculate time range from aggregated data
    const timeRange = calculateDataTimeRange(processedByLocation);
    setDataTimeRange(timeRange);
    
    setLoading(false);
  }, [transactionData, transactionLocationIds, appliedStartTime, appliedEndTime, isToday, chartResolution, calculateDataTimeRange]);

  const maxTransactionTime = useMemo(() => {
    let max = 0;
    Object.values(dataValuesByLocation).forEach(data => {
      data.forEach(item => {
        if (item.transactionTime > max) max = item.transactionTime;
      });
    });
    return max;
  }, [dataValuesByLocation]);

  // Calculate stats per location and overall
  const statsByLocation = useMemo(() => {
    const result = {};

    transactionLocationIds.forEach(locId => {
      const data = dataValuesByLocation[locId] || [];
      if (data.length === 0) {
        result[locId] = { average: 0, peak: 0, current: 0, peakTime: '' };
        return;
      }

      const values = data.map(item => item.transactionTime);
      const average = Math.round(values.reduce((sum, val) => sum + val, 0) / values.length);
      const peak = Math.max(...values);
      const current = values[values.length - 1] || 0;

      const peakIndex = values.indexOf(peak);
      const peakTimestamp = data[peakIndex]?.x;
      const peakTime = peakTimestamp ? new Date(peakTimestamp).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit'
      }) : '';

      result[locId] = { average, peak, current, peakTime };
    });

    return result;
  }, [dataValuesByLocation, transactionLocationIds]);

  // Calculate overall stats across all locations
  const overallStats = useMemo(() => {
    if (transactionLocationIds.length === 0) return { average: 0, peak: 0, current: 0, peakTime: '' };

    // Combine all data points across locations, aligned by time
    const allValues = [];
    const allCurrents = [];

    transactionLocationIds.forEach(locId => {
      const data = dataValuesByLocation[locId] || [];
      data.forEach(item => allValues.push(item.transactionTime));
      if (data.length > 0) {
        allCurrents.push(data[data.length - 1].transactionTime);
      }
    });

    if (allValues.length === 0) return { average: 0, peak: 0, current: 0, peakTime: '' };

    const average = Math.round(allValues.reduce((sum, val) => sum + val, 0) / allValues.length);
    const peak = Math.max(...allValues);
    const current = allCurrents.length > 0 ? Math.round(allCurrents.reduce((sum, val) => sum + val, 0) / allCurrents.length) : 0;

    // Find peak time across all locations
    let peakTime = '';
    transactionLocationIds.forEach(locId => {
      const data = dataValuesByLocation[locId] || [];
      data.forEach(item => {
        if (item.transactionTime === peak && !peakTime) {
          peakTime = new Date(item.x).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit'
          });
        }
      });
    });

    return { average, peak, current, peakTime };
  }, [dataValuesByLocation, transactionLocationIds]);

  // Gets primary text color for chart
  const primaryTextColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-txt').trim();

  // Create game start annotation
  const gameStartAnnotation = currentGame?.date && currentGame?.gameStartTime ? 
    convertGameStartTimeToAnnotation(currentGame.date, currentGame.gameStartTime) : null;

  // Get transaction location labels from currentLocation config
  const getTransactionLocationLabel = (locId) => {
    const transactionZone = currentLocation?.transactionZone;
    if (transactionZone?.shapes) {
      const shape = transactionZone.shapes.find(s => s.id === locId);
      if (shape?.label) return shape.label;
    }
    return transactionLocationIds.length > 1 ? `Register ${locId}` : 'Transaction Time';
  };

  // Get color for a location
  const getLocationColor = (locId) => {
    const index = transactionLocationIds.indexOf(locId);
    return TRANSACTION_COLORS[index % TRANSACTION_COLORS.length];
  };

  // Get combined data values for x-axis labels (use first location)
  const dataValues = useMemo(() => {
    if (transactionLocationIds.length === 0) return [];
    return dataValuesByLocation[transactionLocationIds[0]] || [];
  }, [dataValuesByLocation, transactionLocationIds]);

  const chartData = {
    labels: dataValues.map((item) => item.x),
    datasets: transactionLocationIds.map((locId, index) => ({
      label: getTransactionLocationLabel(locId),
      data: (dataValuesByLocation[locId] || []).map((item) => ({ 
        x: item.x, 
        y: item.transactionTime 
      })),
      ...sectionLineDataset(TRANSACTION_COLORS[index % TRANSACTION_COLORS.length]),
      yAxisID: 'y',
    })),
  };

  const mainChartOptions = {
    scales: {
      x: {
        title: {
          color: primaryTextColor,
          display: true,
          text: "Time",
          padding: { top: 0, bottom: 10 },
        },
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "h A",
          },
          stepSize: chartResolution >= 30 ? 1 : 0.5
        },
        grid: {
          display: false,
        },
        ticks: {
          source: "auto",
          color: primaryTextColor,
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
        },
        min: dataTimeRange.min,
        max: dataTimeRange.max
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Transaction Time (seconds)',
          color: primaryTextColor,
          padding: { top: 0, bottom: 10 },
        },
        grid: {
          display: true,
        },
        ticks: {
          color: primaryTextColor,
          precision: 0,
          beginAtZero: true,
          stepSize: maxTransactionTime < 60 ? 10 : maxTransactionTime < 300 ? 30 : 60,
          callback: function (value) {
            return formatSecondsToMinSec(value);
          },
        },
        max: Math.max(Math.ceil(maxTransactionTime * 1.1), 60),
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        callbacks: {
          title: (context) => {
            if (context.length === 0) {
              return "";
            }
            const originalTimestamp = context[0].raw.x;
            // Parse the timestamp using moment to avoid timezone shifting
            // Data timestamps are in local time (e.g., "2025-03-26T17:45")
            return moment(originalTimestamp).format('h:mm A');
          },
          label: (context) => {
            const value = context.raw.y;
            return ` ${context.dataset.label}: ${formatSecondsToMinSec(value)}`;
          },
        },
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: primaryTextColor,
          usePointStyle: true,
          pointStyle: 'rectRounded',
          generateLabels: function(chart) {
            const defaultLabels = ChartJS.defaults.plugins.legend.labels.generateLabels(chart);
            
            if (gameStartAnnotation) {
              const gameStartLabel = {
                text: 'Game Start',//authCtx.league === "mlb" ? "First Pitch" : authCtx.league === "nfl" ? "Kick Off" : "Game Start",
                fillStyle: 'transparent',
                strokeStyle: CHART_THEME.gameStart,
                lineWidth: 3,
                lineDash: [8, 2],
                pointStyle: 'line',
                hidden: false,
                index: -1
              };
              
              return [gameStartLabel, ...defaultLabels];
            }
            
            return defaultLabels;
          }
        },
        onClick: function(evt, legendItem, legend) {
          if (legendItem.index !== -1) {
            ChartJS.defaults.plugins.legend.onClick.call(this, evt, legendItem, legend);
          }
        }
      },
      zoom: {
        limits: {
          x: { 
            min: dataTimeRange.min, 
            max: dataTimeRange.max, 
            minRange: 60 * 60 * 1000 
          },
        },
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'x',
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
      },
      annotation: {
        annotations: gameStartAnnotation ? [
          {
            type: 'line',
            mode: 'vertical',
            scaleID: 'x',
            value: gameStartAnnotation,
            borderColor: CHART_THEME.gameStart,
            borderWidth: 2,
            borderDash: [8, 4]
          }
        ] : []
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    elements: {
      line: {
        borderWidth: 2,
      },
      point: {
        borderWidth: 1,
        hitRadius: 8,
        hoverRadius: 4,
      },
    },
  };

  useEffect(() => {
    setLoading(true);
    updateDataMap();
  }, [updateDataMap]);

  // Show loading only if we have data but times aren't ready for historical data
  const hasData = transactionData && typeof transactionData === 'object' && transactionLocationIds.length > 0;
  if (!isToday && hasData && (!earliestTime || !latestTime)) {
    return <div>Loading...</div>;
  }

  const hasMultipleLocations = transactionLocationIds.length > 1;

  return (
    <div className="graph panel-2 cx-chart-card">
      <ChartHeader
        title="Transaction times"
        subtitle={isToday ? 'Time per transaction by register area today.' : 'Time per transaction by register area. Use the window to focus on part of the day.'}
        controls={<ResolutionToggle resolutions={resolutions} value={chartResolution} onChange={setChartResolution} />}
      />

      {/* Past days: focus on part of the day (same window as concessions) */}
      {!isToday && earliestTime && latestTime && (
        <div className="rx-chart-window">
          <AnalysisWindow
            earliestTime={earliestTime}
            latestTime={latestTime}
            onRangeChange={handleTimeRangeChange}
            gameStart={dayType === 'game' && currentGame?.date && currentGame?.gameStartTime ? new Date(`${currentGame.date}T${currentGame.gameStartTime}:00`).getTime() : null}
            gatesOpen={dayType === 'game' && currentGame?.date && currentGame?.gatesOpen ? new Date(`${currentGame.date}T${currentGame.gatesOpen}:00`).getTime() : null}
          />
        </div>
      )}

      <div style={{ position: 'relative', height: '300px' }}>
        <Line ref={chartRef} data={chartData} options={applyChartTheme(mainChartOptions)} plugins={[hoverGuidePlugin]} />
      </div>

      {/* Summary per register area (plus overall when there are several) */}
      <div className="rx-loc-stats">
        {transactionLocationIds.map((locId) => {
          const locStats = statsByLocation[locId];
          if (!locStats) return null;
          return (
            <div key={locId} className="rx-loc-row">
              <div className="rx-loc-name">
                <span className="cx-ov-swatch" style={{ background: getLocationColor(locId) }} />
                {getTransactionLocationLabel(locId)}
              </div>
              <StatTile label="Average" value={formatSecondsToMinSec(locStats.average)} />
              <StatTile label="Peak" meta={locStats.peakTime || null} value={formatSecondsToMinSec(locStats.peak)} />
              {isToday && <StatTile label="Current" value={formatSecondsToMinSec(locStats.current)} />}
            </div>
          );
        })}
        {hasMultipleLocations && (
          <div className="rx-loc-row overall">
            <div className="rx-loc-name">Overall</div>
            <StatTile label="Average" value={formatSecondsToMinSec(overallStats.average)} />
            <StatTile label="Peak" meta={overallStats.peakTime || null} value={formatSecondsToMinSec(overallStats.peak)} />
            {isToday && <StatTile label="Current" value={formatSecondsToMinSec(overallStats.current)} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionChart;